<template>
  <v-row>
    <v-col class="mt-2" cols="12" offset-lg="1" lg="10">
      <v-breadcrumbs :items="items" divider=">" class="pl-1 pb-3" />

      <v-card>
        <v-card-text class="theme">
          <v-row>
            <v-col cols="12" lg="2" class="hidden-md-and-down">
              <v-avatar height="130" width="130" v-if="profile_image_url">
                <v-img :src="profile_image_url"></v-img>
              </v-avatar>
              <v-avatar
                height="100"
                width="100"
                dark
                class="white--text grey lighten-1 display-2"
                v-else
              >
                {{ getNameInitials(`${firstName} ${lastName}`) }}
              </v-avatar>
            </v-col>

            <v-col cols="12" sm="12" md="6" lg="4">
              <div class="text-left">
                <div
                  class="title font-weight-regular white--text text-capitalize"
                >
                  {{ firstName }} {{ lastName }}
                </div>
                <div class="subtitle-2 font-weight-regular white--text">
                  {{ jobTitle }}
                </div>
                <div class="subtitle-2 font-weight-regular white--text">
                  {{ workEmail }}
                </div>
                <div class="subtitle-2 font-weight-regular white--text">
                  {{ workPhoneISD ? workPhoneISD.dial_code : "" }}
                  {{ workPhoneNum }}
                </div>
              </div>
            </v-col>

            <v-col cols="12" md="6">
              <div class="text-md-right">
                <div v-if="!isSameUser">
                  <v-btn
                    class="action white--text rounded-sm mr-2"
                    depressed
                    v-if="!isDemo"
                    @click="sendInvite"
                    >Send Invite
                  </v-btn>
                  <v-btn
                    v-if="type !== 'Inactive'"
                    class="error white--text rounded-sm"
                    depressed
                    @click.prevent="deleteWorkflow = true"
                    >Terminate...
                  </v-btn>
                </div>
              </div>
            </v-col>

            <v-col cols="12" sm="12" class="my-0 py-0">
              <v-tabs
                dark
                slider-color="white"
                background-color="#551738"
                v-model="tabModel"
                show-arrows
              >
                <v-tab href="#timeline" :ripple="false" class="white--text">
                  <v-icon class="white--text mr-1">timeline</v-icon>
                  History
                </v-tab>

                <v-tab href="#work" :ripple="false" class="white--text">
                  <v-icon class="white--text mr-1">business</v-icon>
                  Work
                </v-tab>
                <v-tab href="#personal" :ripple="false" class="white--text">
                  <v-icon class="white--text mr-1">account_circle</v-icon>
                  Personal
                </v-tab>
                <v-tab href="#bank" :ripple="false" class="white--text">
                  <v-icon class="white--text mr-1">account_balance</v-icon>
                  Payroll
                </v-tab>
                <v-tab href="#document" :ripple="false" class="white--text">
                  <v-icon class="white--text mr-1">assignment</v-icon>
                  Docs
                </v-tab>
              </v-tabs>
            </v-col>
          </v-row>
        </v-card-text>
        <v-card-text>
          <v-tabs-items v-model="tabModel">
            <v-tab-item :value="'work'">
              <v-card flat>
                <div class="mb-3 mt-n1 text--secondary">
                  Info about the current position, salary, and reporting
                  manager. Updated by the company.
                </div>
                <v-card-text>
                  <v-form ref="workForm" v-model="workFormValidation">
                    <v-row>
                      <v-col cols="12" lg="5">
                        <v-text-field
                          class="my-3"
                          label="Job title*"
                          placeholder="Example: Software Engineer"
                          :rules="[validationRules.required]"
                          :disabled="tempDisable"
                          persistent-hint
                          v-model="jobTitle"
                        />
                        <v-text-field
                          class="my-3"
                          disabled
                          persistent-hint
                          hint="Account activation email will be sent to this email"
                          placeholder="Example: employee@company.com"
                          label="Company email"
                          :rules="[
                            validationRules.required,
                            validationRules.email,
                          ]"
                          v-model="personal.email"
                          type="email"
                        />
                        <v-autocomplete
                          class="my-3"
                          placeholder="Example: Oslo"
                          label="Work location"
                          persistent-hint
                          :items="countries"
                          item-text="name"
                          item-value="name"
                          :disabled="tempDisable"
                          v-model="location"
                        />
                        <v-dialog
                          ref="dialog"
                          v-model="startDateModal"
                          :return-value.sync="startDate"
                          persistent
                          :disabled="tempDisable"
                          width="290px"
                        >
                          <template v-slot:activator="{ on }">
                            <v-text-field
                              persistent-hint
                              label="Job start date"
                              :rules="[validationRules.required]"
                              v-model="computedStartDateFormatted"
                              readonly
                              placeholder="Example: 31-12-2020"
                              v-on="on"
                            />
                          </template>
                          <v-date-picker
                            v-model="startDate"
                            scrollable
                            class="mt-0 pt-0"
                            :first-day-of-week="1"
                          >
                            <v-btn
                              text
                              class="theme-text"
                              @click="startDateModal = false"
                              >Cancel
                            </v-btn>
                            <v-spacer />
                            <v-btn
                              text
                              class="theme-text"
                              @click="$refs.dialog.save(startDate)"
                              >OK
                            </v-btn>
                          </v-date-picker>
                        </v-dialog>
                        <v-select
                          class="my-3"
                          :items="employmentTypes"
                          persistent-hint
                          :rules="[validationRules.required]"
                          v-model="employmentType"
                          :disabled="tempDisable"
                          label="Employment type*"
                          placeholder="Example: Permanent"
                        />
                        <v-text-field
                          class="my-3"
                          label="Employment percentage"
                          persistent-hint
                          placeholder="Example: 100"
                          :disabled="tempDisable"
                          hint="100% = full time employee"
                          v-model.number="percentageEmployed"
                          type="number"
                          :rules="[validationRules.percentageEmployedRule]"
                          suffix="%"
                        />
                      </v-col>
                      <v-col cols="12" lg="7" />
                      <v-col cols="12" lg="5">
                        <v-select
                          class="my-2"
                          placeholder="Example: Accounts"
                          :items="departments"
                          hint="You can add departments in company settings"
                          :rules="[validationRules.required]"
                          v-model="department"
                          :disabled="tempDisable"
                          persistent-hint
                          label="Department*"
                          item-text="name"
                          item-value="name"
                        />
                      </v-col>
                      <v-col cols="12" lg="7">
                        <div class="ml-4 mt-1 grey--text">
                          If you do not see any value here, please go to
                          <router-link
                            :to="{ name: 'ListDepartments' }"
                            style="text-decoration: none"
                            >Department settings
                          </router-link>
                          and add departments.
                        </div>
                      </v-col>
                      <v-col cols="12" lg="5">
                        <v-autocomplete
                          class="my-3"
                          label="Manager*"
                          :rules="[validationRules.required]"
                          persistent-hint
                          :disabled="tempDisable"
                          hint="Reporting manager for the employee"
                          :items="managers"
                          :item-text="managerName"
                          v-model="manager"
                          return-object
                          placeholder="Example: Frank Davis"
                        >
                          <template v-slot:append-outer>
                            <v-tooltip right>
                              <template v-slot:activator="{ on }">
                                <v-icon small class="grey--text pt-1" v-on="on"
                                  >help</v-icon
                                >
                              </template>
                              This will be the HR by default. In future we will
                              add more
                              <br />roles to accommodate more fine grained
                              controls.
                            </v-tooltip>
                          </template>
                        </v-autocomplete>

                        <v-text-field
                          class="my-3"
                          label="Salary"
                          persistent-hint
                          placeholder="Example: 30000"
                          :mask="masks.salary"
                          :disabled="tempDisable"
                          hint="Annual salary of the employee"
                          v-model="salary"
                        />

                        <v-select
                          class="my-3"
                          :items="payFrequencies"
                          hint="Monthly/ BiWeekly/ Annual"
                          v-model="payFrequency"
                          persistent-hint
                          placeholder="Example: Monthly"
                          :disabled="tempDisable"
                          label="Payment Frequency*"
                        />
                      </v-col>
                      <v-col cols="12" lg="7" />
                    </v-row>
                    <v-btn
                      depressed
                      class="save rounded-sm white--text ml-0"
                      :disabled="tempDisable"
                      @click="saveWorkInfoForm"
                      >Save
                    </v-btn>
                  </v-form>
                </v-card-text>
              </v-card>
            </v-tab-item>

            <v-tab-item :value="'bank'">
              <v-card flat>
                <div class="mb-3 mt-n1 text--secondary">
                  Info regarding the bank and account number where salary should
                  be transferred.
                </div>
                <v-card-text>
                  <v-form ref="bankForm" v-model="bankFormValidation">
                    <v-row>
                      <v-col cols="12" lg="5">
                        <v-text-field
                          class="my-3"
                          label="Bank name*"
                          :rules="[validationRules.required]"
                          persistent-hint
                          :disabled="tempDisable"
                          placeholder="Example: DNB"
                          v-model="bankName"
                        />

                        <v-text-field
                          class="my-3"
                          placeholder="Example: 010101010101"
                          label="Bank account number*"
                          :rules="[validationRules.required]"
                          persistent-hint
                          :disabled="tempDisable"
                          hint="Bank account number for salary"
                          v-model="bankAccountNumber"
                        />
                      </v-col>
                      <v-col cols="12" lg="7"></v-col>
                    </v-row>
                    <v-btn
                      depressed
                      class="save rounded-sm white--text ml-0"
                      :disabled="tempDisable"
                      @click="saveBankInfoForm"
                      >Save
                    </v-btn>
                  </v-form>
                </v-card-text>
              </v-card>
            </v-tab-item>

            <v-tab-item :value="'document'">
              <Documents :userInfo="userInfo" :onEdit="true"></Documents>
            </v-tab-item>

            <v-tab-item :value="'timeline'">
              <div class="mb-3 mt-n1 text--secondary">
                A quick overview of the working history at the company. Good for
                record keeping of updates and changes.
              </div>
              <div class="text-right">
                <v-tooltip left>
                  <template v-slot:activator="{ on, attrs }">
                    <v-icon
                      style="z-index: 100"
                      v-bind="attrs"
                      v-on="on"
                      class="action-text"
                      large
                      @click="openForm"
                      >post_add
                    </v-icon>
                  </template>
                  <span>Add a comment on employee's timeline.</span>
                </v-tooltip>
              </div>

              <v-timeline dense align-top v-if="employeeTimeline" class="mt-n9">
                <v-timeline-item
                  color="action"
                  v-for="item in employeeTimeline"
                  :key="item.id"
                  icon="timeline"
                >
                  <div class="theme-text subtitle-1">
                    <div class="mt-0">
                      {{ item.title }}
                      <v-icon
                        class="ml-5"
                        @click="openAgain(item.id)"
                        small
                        v-if="item.editable"
                        >event_note
                      </v-icon>
                      <v-icon
                        class="ml-2"
                        color="error"
                        @click="openDeleteDialog(item.id)"
                        small
                        v-if="item.editable"
                        >delete
                      </v-icon>
                    </div>
                    <div>
                      <div
                        class="text-theme body-2"
                        v-if="item.hidden_for_employee"
                      >
                        Comment is hidden for employee
                      </div>
                      <span class="grey--text text--darken-2 body-2">{{
                        item.date
                      }}</span>
                      <div class="grey--text text--darken-2 body-2">
                        {{ item.description }}
                      </div>
                    </div>
                  </div>
                </v-timeline-item>
              </v-timeline>
            </v-tab-item>
            <!--personal + address tab-->
            <v-tab-item :value="'personal'">
              <v-card flat>
                <div class="mb-3 mt-n1 text--secondary">
                  Personal details that the company needs to know. Remember to
                  keep this updated as life changes.
                </div>
                <v-card-text>
                  <v-form ref="personalForm" v-model="personalFormValidation">
                    <v-row>
                      <v-col cols="12" lg="5">
                        <div class="theme-text my-3 title">Personal</div>
                        <v-text-field
                          label="First name*"
                          :rules="[validationRules.required]"
                          persistent-hint
                          placeholder="Example: John"
                          :disabled="tempDisable"
                          v-model="personal.first_name"
                          class="my-3"
                        />
                        <v-text-field
                          class="my-3"
                          label="Last name"
                          placeholder="Example: Doe"
                          persistent-hint
                          v-model="personal.last_name"
                          :disabled="tempDisable"
                        />
                        <v-row>
                          <v-col cols="12" lg="5" md="5">
                            <v-autocomplete
                              class="my-3"
                              label="Phone prefix (country)"
                              placeholder="Example: Norway"
                              :rules="[validationRules.required]"
                              persistent-hint
                              :items="countries"
                              :item-text="isdCodeName"
                              v-model="personal.phone_isd"
                              :return-object="true"
                              :disabled="tempDisable"
                            >
                              <template v-slot:append-outer>
                                <v-tooltip right>
                                  <template v-slot:activator="{ on }">
                                    <v-icon
                                      small
                                      class="grey--text pt-1"
                                      v-on="on"
                                      >help</v-icon
                                    >
                                  </template>
                                  Select the country for the phone number.
                                  <br />We will automatically fetch the correct
                                  country code.
                                </v-tooltip>
                              </template>
                            </v-autocomplete>
                          </v-col>
                          <v-col cols="12" lg="7" md="7">
                            <v-text-field
                              class="my-3"
                              label="Mobile number"
                              placeholder="Example: 90909090"
                              :rules="[
                                validationRules.required,
                                validatePhoneNumberRule,
                              ]"
                              v-model="personal.phone_num"
                              :disabled="tempDisable"
                              validate-on-blur
                            >
                              <template v-slot:append-outer>
                                <v-tooltip right>
                                  <template v-slot:activator="{ on }">
                                    <v-icon
                                      small
                                      class="grey--text pt-1"
                                      v-on="on"
                                      >help</v-icon
                                    >
                                  </template>
                                  Mobile number. Please DO NOT add the country
                                  code
                                  <br />For ex: use 90909090 instead of
                                  +4790909090
                                </v-tooltip>
                              </template>
                            </v-text-field>
                          </v-col>
                        </v-row>
                        <v-dialog
                          ref="dialog"
                          v-model="modal"
                          :return-value.sync="personal.date_of_birth"
                          persistent
                          width="290px"
                        >
                          <template v-slot:activator="{ on }">
                            <v-text-field
                              persistent-hint
                              label="Date of birth"
                              :rules="[validationRules.required]"
                              v-model="computedDateFormatted"
                              readonly
                              placeholder="Example: 31-12-2020"
                              v-on="on"
                            >
                              <template v-slot:append-outer>
                                <v-tooltip right>
                                  <template v-slot:activator="{ on }">
                                    <v-icon
                                      small
                                      class="grey--text pt-1"
                                      v-on="on"
                                      >help</v-icon
                                    >
                                  </template>
                                  We will send you notifications on upcoming
                                  birthdays using this field.
                                  <br />Should be provided in dd-mm-yyyy format.
                                </v-tooltip>
                              </template>
                            </v-text-field>
                          </template>
                          <v-date-picker
                            v-model="personal.date_of_birth"
                            scrollable
                            class="mt-0 pt-0"
                            :first-day-of-week="1"
                          >
                            <v-btn
                              text
                              class="theme-text"
                              @click="modal = false"
                              >Cancel</v-btn
                            >
                            <v-spacer />
                            <v-btn
                              text
                              class="theme-text"
                              @click="$refs.dialog.save(personal.date_of_birth)"
                              >OK</v-btn
                            >
                          </v-date-picker>
                        </v-dialog>
                        <v-select
                          class="my-3"
                          :items="genders"
                          v-model="personal.gender"
                          persistent-hint
                          :disabled="tempDisable"
                          label="Gender"
                          placeholder="Example: Female"
                        >
                          <template v-slot:append-outer>
                            <v-tooltip right>
                              <template v-slot:activator="{ on }">
                                <v-icon small class="grey--text pt-1" v-on="on"
                                  >help</v-icon
                                >
                              </template>
                              We use this field to generate anonymous
                              statistics.
                            </v-tooltip>
                          </template>
                        </v-select>

                        <v-text-field
                          class="my-3"
                          label="Social security number*"
                          :rules="[validationRules.required]"
                          persistent-hint
                          placeholder="Example: 01129955131"
                          v-model="personal.ssn"
                          :disabled="tempDisable"
                        >
                          <template v-slot:append-outer>
                            <v-tooltip right>
                              <template v-slot:activator="{ on }">
                                <v-icon small class="grey--text pt-1" v-on="on"
                                  >help</v-icon
                                >
                              </template>
                              In future, we will use this field to automatically
                              generate employee
                              <br />contracts and other documents, which you can
                              use for official purposes.
                            </v-tooltip>
                          </template>
                        </v-text-field>
                        <v-autocomplete
                          class="my-3"
                          label="Nationality"
                          placeholder="Example: Norway"
                          persistent-hint
                          :items="countries"
                          item-text="name"
                          item-value="name"
                          v-model="personal.nationality"
                          :return-object="true"
                          :disabled="tempDisable"
                        >
                        </v-autocomplete>
                        <v-select
                          class="my-3"
                          :items="sizes"
                          v-model="personal.size"
                          persistent-hint
                          :disabled="tempDisable"
                          label="T-shirt size"
                          placeholder="Example: S"
                        >
                        </v-select>
                        <v-text-field
                          class="my-3"
                          label="Allergies"
                          v-model="personal.allergies"
                          :disabled="tempDisable"
                        />
                        <v-text-field
                          class="my-3"
                          label="Food preference"
                          v-model="personal.food_preference"
                          :disabled="tempDisable"
                        />
                        <v-textarea
                          class="my-3"
                          label="Personal interests & hobbies"
                          v-model="personal.interests"
                          :disabled="tempDisable"
                        />
                        <div class="theme-text my-5 title">
                          Emergency contact
                        </div>
                        <v-text-field
                          label="Emergency contact name"
                          persistent-hint
                          class="my-3"
                          v-model="personal.emergency_contact_name"
                          :disabled="tempDisable"
                          placeholder="Example: Jane Doe"
                        />

                        <v-text-field
                          label="Emergency contact email"
                          class="my-3"
                          :rules="[validationRules.email]"
                          :disabled="tempDisable"
                          placeholder="Example: jane.doe@email.com"
                          v-model="personal.emergency_contact_email"
                        />
                        <v-row>
                          <v-col cols="12" lg="5" md="5">
                            <v-autocomplete
                              label="Phone prefix emergency contact"
                              placeholder="Example: Norway"
                              persistent-hint
                              class="my-3"
                              :disabled="tempDisable"
                              :items="countries"
                              :item-text="isdCodeName"
                              :return-object="true"
                              v-model="personal.emergency_contact_phone_isd"
                            >
                              <template v-slot:append-outer>
                                <v-tooltip right>
                                  <template v-slot:activator="{ on }">
                                    <v-icon
                                      small
                                      class="grey--text pt-1"
                                      v-on="on"
                                      >help</v-icon
                                    >
                                  </template>
                                  Select the country for the phone number.
                                  <br />We will automatically fetch the correct
                                  country code.
                                </v-tooltip>
                              </template>
                            </v-autocomplete>
                          </v-col>
                          <v-col cols="12" lg="7" md="7">
                            <v-text-field
                              label="Emergency contact mobile"
                              persistent-hint
                              class="my-3"
                              :rules="[emergencyPhoneNumberRule]"
                              validate-on-blur
                              :disabled="tempDisable"
                              placeholder="Example: 90909090"
                              v-model="personal.emergency_contact_phone_num"
                            >
                              <template v-slot:append-outer>
                                <v-tooltip right>
                                  <template v-slot:activator="{ on }">
                                    <v-icon
                                      small
                                      class="grey--text pt-1"
                                      v-on="on"
                                      >help</v-icon
                                    >
                                  </template>
                                  Mobile number. Please DO NOT add the country
                                  code
                                  <br />For ex: use 90909090 instead of
                                  +4790909090
                                </v-tooltip>
                              </template>
                            </v-text-field>
                          </v-col>
                        </v-row>
                      </v-col>

                      <v-col cols="12" lg="6" class="offset-lg-1">
                        <div class="theme-text my-3 title">Address</div>
                        <v-text-field
                          class="my-3"
                          label="Address line 1*"
                          :disabled="tempDisable"
                          :rules="[validationRules.required]"
                          placeholder="Example : Fredlundveien 48"
                          persistent-hint
                          v-model="personal.line1"
                        />

                        <v-text-field
                          class="my-3"
                          label="Address line 2"
                          v-model="personal.line2"
                          :disabled="tempDisable"
                        />

                        <v-text-field
                          class="my-3"
                          label="City*"
                          :rules="[validationRules.required]"
                          persistent-hint
                          :disabled="tempDisable"
                          placeholder="Example: Bergen"
                          v-model="personal.city"
                        />

                        <v-text-field
                          class="my-3"
                          label="Postcode*"
                          :rules="[validationRules.required]"
                          :disabled="tempDisable"
                          persistent-hint
                          placeholder="Example : 3122"
                          v-model="personal.post_code"
                        />

                        <v-text-field
                          class="my-3"
                          label="State"
                          persistent-hint
                          placeholder="Example: Bergen"
                          :disabled="tempDisable"
                          v-model="personal.state"
                        />

                        <v-autocomplete
                          class="my-3"
                          label="Country*"
                          :rules="[validationRules.required]"
                          :disabled="tempDisable"
                          persistent-hint
                          placeholder="Example: Norway"
                          :items="countries"
                          item-text="name"
                          item-value="name"
                          v-model="personal.country"
                        />
                      </v-col>
                      <v-btn
                        depressed
                        class="save rounded-sm white--text ml-0"
                        :disabled="tempDisable"
                        @click="savePersonal"
                        >Save
                      </v-btn>
                      <v-col cols="12" lg="7" />
                    </v-row>
                  </v-form>
                </v-card-text>
              </v-card>
            </v-tab-item>
          </v-tabs-items>
        </v-card-text>
      </v-card>
    </v-col>

    <v-dialog v-model="dialogEdit" persistent max-width="400">
      <v-card flat>
        <v-card-title class="headline pt-4 theme white--text">
          <span>Edit comment</span>
          <v-spacer />
          <v-btn icon @click="dialogEdit = !dialogEdit">
            <v-icon class="white--text">close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text class="my-3 body-2">
          <v-form ref="EditNoteForm" v-model="noteFormValidation">
            <v-row>
              <v-col cols="12">
                <v-textarea
                  class="my-3"
                  label="Note*"
                  :rules="[validationRules.required]"
                  persistent-hint
                  hint="Provide a comment for the employee"
                  :disabled="tempDisable"
                  placeholder="Example: Completed 1:1 with employee."
                  v-model="note.description"
                />
                <v-switch
                  v-model="note.hidden_for_employee"
                  class="mx-2"
                  :label="noteLabel()"
                />
              </v-col>
            </v-row>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn class="save rounded-sm white--text" text @click="editNote"
            >Save</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="dialogDelete" persistent max-width="400">
      <v-card flat>
        <v-card-title class="headline pt-4 theme white--text">
          <span>Are your sure</span>
          <v-spacer />
          <v-btn icon @click="dialogDelete = !dialogDelete">
            <v-icon class="white--text">close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text class="my-3 body-2">
          Confirm if you want to delete this note permanently.
        </v-card-text>
        <v-card-actions>
          <v-btn class="action rounded-sm white--text" text @click="deleteNote"
            >Delete</v-btn
          >
          <v-spacer />
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="dialog" persistent max-width="550">
      <v-card flat>
        <v-card-title class="headline pt-4 theme white--text">
          <span>Add a comment on employee's timeline</span>
          <v-spacer />
          <v-btn icon @click="dialog = !dialog">
            <v-icon class="white--text">close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text class="my-3 body-2">
          <div class="body-2">
            Comment on a recent employee change, performance review etc.
          </div>
          <v-form ref="noteForm" v-model="noteFormValidation">
            <v-row>
              <v-col cols="12">
                <v-textarea
                  class="my-3"
                  label="Note*"
                  :rules="[validationRules.required]"
                  :disabled="tempDisable"
                  placeholder="Example: Completed 1:1 with employee."
                  autofocus
                  v-model="note.description"
                />
                <div>Choose if comment should be hidden from the employee.</div>
                <v-switch
                  v-model="note.hidden_for_employee"
                  :label="noteLabel()"
                />
              </v-col>
            </v-row>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn class="save rounded-sm white--text" text @click="saveNote"
            >Save</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="deleteWorkflow" persistent max-width="700">
      <v-card>
        <v-card-title class="theme">
          <span class="white--text">Start employee off boarding</span>
          <v-spacer />
          <v-btn icon @click="deleteWorkflow = false">
            <v-icon class="white--text">close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text>
          <div class="mt-5 mb-4 grey--text text--darken-3 body-1">
            Please confirm what action would you like to take.
          </div>

          <v-row>
            <v-col cols="9">
              <div class="theme-text">Inactivate employee</div>
              <div>
                <div class="mb-n1">
                  This option will disable login for user but preserve user
                  details.
                </div>
                <small
                  >e.g. When you want to deactivate user account without
                  initiating offboarding workflow.</small
                >
              </div>
            </v-col>
            <v-col cols="3" class="text-right">
              <v-btn
                class="action white--text rounded-sm"
                @click="startOffBoarding(false)"
                depressed
                >Inactivate
              </v-btn>
            </v-col>

            <v-col cols="12" class="my-0">
              <v-divider />
            </v-col>

            <v-col cols="8">
              <div class="theme-text">Initiate offboarding workflow?</div>
              <div>Start initiating offboarding tasks for the employee</div>
              <small
                >The user account will be deactivated when all offboarding tasks
                complete.</small
              >
            </v-col>
            <v-col cols="4" class="text-right">
              <v-btn
                class="action white--text rounded-sm mt-1"
                @click="showDeleteStepper = !showDeleteStepper"
                depressed
                v-text="!showDeleteStepper ? 'start offboarding' : 'cancel'"
              />
            </v-col>
            <v-col cols="12" class="my-0">
              <v-divider />
            </v-col>
            <v-col cols="10">
              <div class="theme-text">Delete employee record?</div>
              <div>
                <div class="mb-n1">
                  Use this option to permanently remove employee data from the
                  system.
                </div>
              </div>
            </v-col>
            <v-col cols="2">
              <v-btn
                class="action white--text rounded-sm"
                @click="deleteEntity"
                depressed
                >delete</v-btn
              >
            </v-col>
            <v-col cols="12">
              <v-expand-transition>
                <template v-if="showDeleteStepper">
                  <div v-if="workflows && workflows.length > 0">
                    <v-simple-table class="border-gray mt-6">
                      <template v-slot:default>
                        <thead class="theme">
                          <tr>
                            <th class="text-left white--text pl-3 body-1">
                              Name
                            </th>
                            <th class="text-right white--text pr-3 body-1">
                              Assigned To
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr
                            v-for="item in workflows[0].tasks"
                            :key="item.name"
                          >
                            <td class="pb-0 pl-3">{{ item.name }}</td>
                            <td class="py-0 mt-0" style="width: 60%">
                              <div class="mt-1 mb-n5">
                                <v-select
                                  v-if="managers"
                                  :items="managers"
                                  :item-text="managerFirstName"
                                  item-value="id"
                                  v-model="item.assigned_to_id"
                                  dense
                                  outlined
                                />
                              </div>
                            </td>
                          </tr>
                        </tbody>
                      </template>
                    </v-simple-table>
                    <div class="row">
                      <div class="col-sm-12 mt-3 text-right">
                        <v-btn
                          depressed
                          @click="startOffBoarding(true)"
                          class="save white--text rounded-sm"
                          >Confirm</v-btn
                        >
                      </div>
                    </div>
                  </div>
                  <div v-else class="title">
                    <v-alert
                      border="left"
                      text
                      type="warning"
                      class="theme body-1 mt-2 white--text"
                    >
                      You have not created any workflows yet. You can start by
                      creating new workflows
                      <a href="/#/company/workflows" class="action-text">here</a
                      >.
                    </v-alert>
                  </div>
                </template>
              </v-expand-transition>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>
    </v-dialog>
    <v-snackbar v-model="snackbar" :bottom="true" :right="true">
      {{ snackbarMessage }}
      <v-btn color="pink" text @click="snackbar = false">Close</v-btn>
    </v-snackbar>
  </v-row>
</template>

<script>
import libphonenumber from "google-libphonenumber";
import { mapGetters } from "vuex";

import { Actions } from "@/views/actions";
import { Breadcrumbs } from "@/views/breadcrumbs";
import { Component } from "@/views/component";
import { Constants } from "@/views/constants";
import Documents from "@/views/protected/employee/Documents";

export default {
  name: Component.Protected.Employee.Edit,
  components: { Documents },
  data: () => ({
    dialogEdit: false,
    dialogDelete: false,
    wfs: [],
    showDeleteStepper: false,
    deleteWorkflow: false,
    tabModel: "",
    modal: false,
    startDateModal: false,
    managers: [],
    items: Breadcrumbs.Employee.Edit,
    // deleteDialog: false,
    // form values
    genders: [],
    officeTypes: [],
    employmentTypes: [],
    payFrequencies: [],
    departments: [],
    sizes: [],
    // bank info
    bankName: null,
    ssnNumber: null,
    bankAccountNumber: null,
    // Work info
    workPhoneISD: null,
    workPhoneNum: null,
    // workEmail: null,
    startDate: "",
    employmentType: null,
    jobTitle: null,
    location: null,
    department: null,
    manager: null,
    salary: null,
    percentageEmployed: null,
    payFrequency: null,
    workEmail: null,
    // personal info
    username: null,
    firstName: null,
    lastName: null,
    email: null,
    id: null,
    type: null,
    isHR: null,
    isEmployee: null,
    isManager: null,
    setPrototypeOf: null,
    // address information
    // from Firebase
    headers: {},
    // form validation
    workFormValidation: true,
    bankFormValidation: true,
    personalFormValidation: true,
    // snackbar
    snackbar: false,
    snackbarMessage: "",
    tempDisable: false,
    phoneNumberValidator: null,
    profile_image_url: null,
    tnc_complete: false,

    // notes
    dialog: false,
    noteFormValidation: true,
    note: {
      id: "",
      user_id: 0,
      description: "",
      hidden_for_employee: null,
    },
    employees: [],
    delete_id: 0,

    personal: {
      user_id: null,
      email: null,
      phone_isd: null,
      phone_num: null,
      gender: null,
      first_name: null,
      last_name: null,
      date_of_birth: null,
      ssn: null,
      size: null,
      allergies: null,
      food_preference: null,
      interests: null,
      nationality: null,

      //Personal emergency
      emergency_contact_name: null,
      emergency_contact_phone_isd: null,
      emergency_contact_phone_num: null,
      emergency_contact_email: null,
      //Personal address
      address_id: null,
      city: null,
      line1: null,
      line2: null,
      state: null,
      country: null,
      post_code: null,
      type: null,
    },
  }),
  computed: {
    ...mapGetters([
      "userInfo",
      "companyInfo",
      "countries",
      "employeeTimeline",
      "workflows",
    ]),
    computedStartDateFormatted() {
      return this.datePickerFormat(this.startDate);
    },
    computedDateFormatted() {
      return this.datePickerFormat(this.personal.date_of_birth);
    },

    validatePhoneNumberRule() {
      if (!this.personal.phone_num) {
        return Constants.PhoneNumber.Messages.Error.Required;
      }
      if (isNaN(this.personal.phone_num))
        return Constants.PhoneNumber.Messages.Error.InvalidFormat;
      if (this.personal.phone_num.length < 8) {
        return Constants.PhoneNumber.Messages.Error.InvalidFormat;
      }
      if (!this.personal.phone_isd || !this.personal.phone_isd.code)
        return Constants.PhoneNumber.Messages.Error.InvalidCountryCode;

      const num = this.phoneValidator.parseAndKeepRawInput(
        this.personal.phone_num,
        this.personal.phone_isd.code
      );
      if (!num) return Constants.PhoneNumber.Messages.Error.InvalidFormat;
      if (this.phoneValidator.isValidNumber(num)) return true;

      return Constants.PhoneNumber.Messages.Error.InvalidCountryCode;
    },

    emergencyPhoneNumberRule() {
      // if none then true
      if (
        !this.personal.emergency_contact_phone_isd &&
        !this.personal.emergency_contact_phone_num
      ) {
        return true;
      }

      // Both ISD and Num should be filled
      if (
        this.personal.emergency_contact_phone_isd &&
        !this.personal.emergency_contact_phone_num
      )
        return Constants.PhoneNumber.Messages.Error.Required;

      if (
        !this.personal.emergency_contact_phone_isd &&
        this.personal.emergency_contact_phone_num
      )
        return Constants.PhoneNumber.Messages.Error.SelectISD;

      if (isNaN(this.personal.emergency_contact_phone_num))
        return Constants.PhoneNumber.Messages.Error.InvalidFormat;

      if (
        this.personal.emergency_contact_phone_num &&
        this.personal.emergency_contact_phone_num.length < 8
      )
        return Constants.PhoneNumber.Messages.Error.InvalidFormat;

      if (
        !this.personal.emergency_contact_phone_num ||
        !this.personal.emergency_contact_phone_isd.code
      )
        return Constants.PhoneNumber.Messages.Error.InvalidCountryCode;

      const num = this.phoneValidator.parseAndKeepRawInput(
        this.personal.emergency_contact_phone_num,
        this.personal.emergency_contact_phone_isd.code
      );
      if (!num) return Constants.PhoneNumber.Messages.Error.InvalidFormat;
      if (this.phoneValidator.isValidNumber(num)) return true;
      return Constants.PhoneNumber.Messages.Error.InvalidCountryCode;
    },
    // workPhoneNumberRule() {
    //   if (!this.workPhoneISD && !this.workPhoneNum) {
    //     return true;
    //   }
    //   // Both ISD and Num should be filled
    //   if (this.workPhoneISD && !this.workPhoneNum)
    //     return Constants.PhoneNumber.Messages.Error.Required;
    //   if (!this.workPhoneISD && this.workPhoneNum)
    //     return Constants.PhoneNumber.Messages.Error.SelectISD;
    //   if (isNaN(this.workPhoneNum)) {
    //     return Constants.PhoneNumber.Messages.Error.InvalidFormat;
    //   }
    //   if (this.workPhoneNum && this.workPhoneNum.length < 8)
    //     return Constants.PhoneNumber.Messages.Error.InvalidFormat;
    //   if (!this.workPhoneNum || !this.workPhoneISD.code)
    //     return Constants.PhoneNumber.Messages.Error.InvalidCountryCode;

    //   const num = this.phoneValidator.parseAndKeepRawInput(
    //     this.workPhoneNum,
    //     this.workPhoneISD.code
    //   );
    //   if (!num) return Constants.PhoneNumber.Messages.Error.InvalidFormat;
    //   if (this.phoneValidator.isValidNumber(num)) return true;
    //   return Constants.PhoneNumber.Messages.Error.InvalidCountryCode;
    // },
    isSameUser() {
      return (
        this.userInfo.role === Constants.Roles.HR &&
        this.$route.params.id == this.userInfo.userID
      );
    },
    companyActive() {
      return this.companyInfo.status === "Active";
    },
    isDemo() {
      return this.userInfo.role === Constants.Roles.HR && !this.companyActive;
    },
  },
  methods: {
    noteLabel() {
      return this.note.hidden_for_employee
        ? "Comment hidden"
        : "Comment visible to the employee";
    },
    isdCodeName(country) {
      if (country) {
        return `${country.dial_code} - ${country.name}`;
      }
    },
    deleteNote() {
      this.$store
        .dispatch(Actions.Timeline.Delete, this.delete_id)
        .finally(() => {
          this.delete_id = 0;
          this.dialogDelete = false;
          this.getTimeline();
        });
    },
    openDeleteDialog(id) {
      this.delete_id = id;
      this.dialogDelete = true;
    },
    openAgain(id) {
      if (id) {
        this.$store.dispatch(Actions.Timeline.Get, id).then((r) => {
          if (r.status === 200) {
            this.note = {
              id: r.data.timeline_id,
              user_id: r.data.user_id,
              description: r.data.description,
              hidden_for_employee: r.data.hidden_for_employee,
            };
            this.dialogEdit = true;
          }
        });
      }
    },
    editNote() {
      if (this.$refs.EditNoteForm.validate()) {
        let payload = {
          id: this.note.id,
          data: {
            timeline_id: this.note.id,
            description: this.note.description,
            user_id: this.note.user_id,
            hidden_for_employee: this.note.hidden_for_employee,
          },
        };

        this.$store
          .dispatch(Actions.Timeline.Put, payload)
          .then(() => {
            this.getTimeline();
          })
          .catch((err) => {
            console.error(err);
            this.snackbar = true;
            this.snackbarMessage =
              "Could not save timeline entry. Please try again.";
          })
          .finally(() => {
            this.dialogEdit = false;
            this.note = {};
            this.getTimeline();
          });
      }
    },
    managerFirstName(e) {
      return `${e.first_name} ${e.last_name}`;
    },
    startOffBoarding(withTasks) {
      let payload = {
        id: this.$route.params.id,
        data: {
          tasks:
            this.workflows && this.workflows.length > 0
              ? this.workflows[0].tasks.map((x) => {
                  return {
                    description: x.description,
                    name: x.name,
                    assigned_to_id: x.assigned_to_id,
                  };
                })
              : [],
          with_tasks: withTasks === true,
          workflow_id:
            this.workflows && this.workflows.length > 0
              ? parseInt(this.workflows[0].id)
              : 0,
        },
      };

      this.$store.dispatch(Actions.Employee.OffBoarding, payload).then((r) => {
        this.deleteWorkflow = false;
        this.$router.push({ name: Component.Protected.Employee.Base });
      });
    },
    getWorkflows() {
      this.$store.dispatch(Actions.Workflow.Find, {
        limit: 1,
        offset: 0,
        type: "OffBoarding",
      });
    },
    saveNote() {
      if (this.$refs.noteForm.validate()) {
        this.note.user_id = parseInt(this.$route.params.id);
        this.$store
          .dispatch(Actions.Employee.AddToTimeline, { ...this.note })
          .then(() => {
            this.getTimeline();
          })
          .catch((err) => {
            console.error(err);
            this.snackbar = true;
            this.snackbarMessage =
              "Could not add timeline entry. Please try again.";
          })
          .finally(() => {
            this.dialog = false;
          });
      }
    },
    openForm() {
      this.resetNote();
      this.dialog = true;
    },
    managerName(item) {
      const fName = item && item.first_name ? item.first_name : "";
      const lName = item && item.last_name ? item.last_name : "";
      return `${fName} ${lName}`;
    },
    resetNote() {
      this.note = {
        user_id: 0,
        description: "",
        hidden_for_employee: true,
      };
    },
    getPersonal() {
      this.$store
        .dispatch(Actions.Employee.Personal, this.$route.params.id)
        .then((r) => {
          if (r.status === 200 && r.data) {
            //Profile
            this.personal.email = r.data.profile_info.work_email
              ? r.data.profile_info.work_email
              : r.data.profile_info.email;
            this.personal.first_name = r.data.profile_info.first_name;
            this.personal.last_name = r.data.profile_info.last_name;
            this.personal.phone_isd = this.getISDStuff(
              r.data.profile_info.phone_isd
            );
            this.personal.phone_num = r.data.profile_info.phone_num;
            let dob = r.data.profile_info.date_of_birth;
            if (!dob) {
              this.personal.date_of_birth = null;
            } else if (dob.substring(0, 10) === "0001-01-01") {
              this.personal.date_of_birth = null;
            } else {
              this.personal.date_of_birth = dob.substring(0, 10);
            }
            this.personal.gender = r.data.profile_info.gender;
            this.personal.ssn = r.data.profile_info.ssn_number;
            this.personal.size = r.data.profile_info.size;
            this.personal.allergies = r.data.profile_info.allergies;
            this.personal.food_preference = r.data.profile_info.food_preference;
            this.personal.interests = r.data.profile_info.interests;
            this.personal.nationality = r.data.profile_info.nationality;

            //Emergency
            this.personal.emergency_contact_name =
              r.data.emergency_info.emergency_contact_name;
            this.personal.emergency_contact_phone_isd = this.getISDStuff(
              r.data.emergency_info.emergency_contact_phone_isd
            );
            this.personal.emergency_contact_phone_num =
              r.data.emergency_info.emergency_contact_phone_num;
            this.personal.emergency_contact_email =
              r.data.emergency_info.emergency_contact_email;

            //Address
            this.personal.address_id = r.data.address_info.address_id;
            this.personal.line1 = r.data.address_info.line_1;
            this.personal.line2 = r.data.address_info.line_2;
            this.personal.city = r.data.address_info.city;
            this.personal.state = r.data.address_info.state;
            this.personal.post_code = r.data.address_info.post_code;
            this.personal.country = r.data.address_info.country;
            this.personal.type = r.data.address_info.type;
          }
        })
        .catch((err) => console.error(err));
    },
    getInfo() {
      this.$store
        .dispatch(Actions.Employee.Info)
        .then((r) => {
          if (r.status === 200 && r.data) {
            this.genders = r.data.genders;
            this.employmentTypes = r.data.employment_types;
            this.payFrequencies = r.data.payment_frequencies;
            this.officeTypes = r.data.address_types;
            this.departments = r.data.departments;
            this.sizes = r.data.sizes;
          }
        })
        .catch(() => {
          this.snackbarMessage = "Could not get additional info";
          this.snackbar = true;
          this.genders = Constants.Employee.Genders.All;
          this.employmentTypes = Constants.Employee.EmploymentType.All;
          this.payFrequencies = Constants.Employee.PaymentFrequencies.All;
          this.officeTypes = Constants.Employee.AddressType.All;
          this.departments = [];
          this.sizes = Constants.Employee.Sizes.All;
        });
    },
    resetModels() {
      // snackbar
      this.snackbar = false;
      this.snackbarMessage = "";

      // personal
      this.username = null;
      this.firstName = null;
      this.lastName = null;
      this.email = null;
      this.id = null;
      this.type = null;
      this.isHR = null;
      this.isEmployee = null;
      this.isManager = null;

      // work info
      this.workPhoneISD = null;
      this.workPhoneNum = null;
      // this.workEmail = null;
      this.startDate = "";
      this.employmentType = null;
      this.jobTitle = null;
      this.location = null;
      this.department = null;
      this.manager = null;
      this.salary = 0.0;
      this.payFrequency = null;
      this.percentageEmployed = 0;

      // bank info
      this.bankName = null;
      this.ssnNumber = null;
      this.bankAccountNumber = null;

      // form validations
      this.personalFormValidation = true;
      this.workFormValidation = true;
      this.bankFormValidation = true;

      this.phoneValidator = libphonenumber.PhoneNumberUtil.getInstance();
      this.deleteWorkflow = false;

      //Personal profile
      this.personal.email = null;
      this.personal.phone_isd = null;
      this.personal.phone_num = null;
      this.personal.gender = null;
      this.personal.first_name = null;
      this.personal.last_name = null;
      this.personal.date_of_birth = null;
      this.personal.ssn = null;
      this.personal.size = null;
      this.personal.allergies = null;
      this.personal.food_preference = null;
      this.personal.interests = null;

      //Personal emergency
      this.personal.emergency_contact_name = null;
      this.personal.emergency_contact_phone_isd = null;
      this.personal.emergency_contact_phone_num = null;
      this.personal.emergency_contact_email = null;

      //Personal address
      this.personal.address_id = null;
      this.personal.city = null;
      this.personal.line1 = null;
      this.personal.line2 = null;
      this.personal.state = null;
      this.personal.country = null;
      this.personal.post_code = null;
      this.personal.type = null;
    },

    savePersonal() {
      if (this.$refs.personalForm.validate()) {
        let profile_info = {
          user_id: parseInt(this.$route.params.id),
          email: this.personal.email,
          first_name: this.personal.first_name,
          last_name: this.personal.last_name,
          date_of_birth: this.computedDateFormatted
            ? this.computedDateFormatted
            : null,
          phone_isd: this.personal.phone_isd.dial_code,
          phone_num: this.personal.phone_num,
          gender: this.personal.gender,
          ssn_number: this.personal.ssn,
          size: this.personal.size,
          allergies: this.personal.allergies,
          food_preference: this.personal.food_preference,
          interests: this.personal.interests,
          nationality: this.personal.nationality.name,
        };

        let emergency_info = {
          emergency_contact_name: this.personal.emergency_contact_name,

          emergency_contact_phone_isd:
            this.personal.emergency_contact_phone_isd &&
            this.personal.emergency_contact_phone_isd.dial_code
              ? this.personal.emergency_contact_phone_isd.dial_code
              : "",

          emergency_contact_phone_num:
            this.personal.emergency_contact_phone_num,
          emergency_contact_email: this.personal.emergency_contact_email,
        };

        let address_info = {
          address_id: parseInt(this.personal.address_id),
          type: this.personal.type
            ? this.personal.type
            : Constants.Employee.AddressType.Home,
          line_1: this.personal.line1,
          line_2: this.personal.line2,
          city: this.personal.city,
          state: this.personal.state,
          post_code: this.personal.post_code,
          country: this.personal.country,
        };
        this.tempDisable = true;
        let payload = {
          profile_info,
          address_info,
          emergency_info,
        };
        this.$store
          .dispatch(Actions.Employee.UpdatePersonal, payload)
          .then(() => {
            this.snackbarMessage = Constants.Employee.Messages.Info.Personal;
          })
          .catch((err) => {
            console.error(Constants.Employee.Messages.Error.Edit, err);
            this.snackbarMessage = Constants.Employee.Messages.Error.Edit;
          })
          .finally(() => {
            this.snackbar = true;
            this.tempDisable = false;
            this.getPersonal();
            this.getEmployee();
          });
      }
    },
    saveWorkInfoForm() {
      if (this.$refs.workForm.validate()) {
        let data = {
          start_date: this.computedStartDateFormatted
            ? this.computedStartDateFormatted
            : null,
          employment_type: this.employmentType,
          job_title: this.jobTitle,
          job_location: this.location,
          department: this.department,
          manager: this.managerName(this.manager),
          manager_id: parseInt(this.manager.id),
          pay_frequency: this.payFrequency,
          id: parseInt(this.$route.params.id),
          salary: parseFloat(this.salary),
          percentage_employed: parseInt(this.percentageEmployed),
          tnc_complete: this.tnc_complete,
        };
        this.tempDisable = true;
        this.$store
          .dispatch(Actions.Employee.Save, {
            id: this.$route.params.id,
            data: data,
          })
          .then(() => {
            this.snackbarMessage = Constants.Employee.Messages.Info.Work;
          })
          .catch((err) => {
            console.error(Constants.Employee.Messages.Error.Edit, err);
            this.snackbarMessage = Constants.Employee.Messages.Error.Edit;
          })
          .finally(() => {
            this.snackbar = true;
            this.tempDisable = false;
          });
      }
    },

    saveBankInfoForm() {
      if (this.$refs.bankForm.validate()) {
        let data = {
          bank_name: this.bankName,
          bank_account_number: this.bankAccountNumber,
          id: parseInt(this.$route.params.id),
          tnc_complete: this.tnc_complete,
        };
        if (!data.bank_name && !data.bank_account_number) {
          this.temporaryDisable = false;
          return false;
        }
        this.tempDisable = true;
        this.$store
          .dispatch(Actions.Employee.Save, {
            id: this.$route.params.id,
            data: data,
          })
          .then(() => {
            this.snackbarMessage = Constants.Employee.Messages.Info.Bank;
          })
          .catch((err) => {
            console.error(Constants.Employee.Messages.Error.Edit, err);
            this.snackbarMessage = Constants.Employee.Messages.Error.Edit;
          })
          .finally(() => {
            this.snackbar = true;
            this.tempDisable = false;
          });
      }
    },
    getISDStuff(countryCode) {
      return this.countries.filter((e) => e.dial_code === countryCode)[0];
    },
    getEmployee() {
      this.$store
        .dispatch(Actions.Employee.Get, this.$route.params.id)
        .then((r) => {
          if (r.status === 200) {
            this.username = r.data.email;
            this.firstName = r.data.first_name;
            this.lastName = r.data.last_name;
            this.email = r.data.work_email ? r.data.work_email : r.data.email;
            this.type = r.data.type;
            this.isHR = r.data.is_hr;
            this.isEmployee = r.data.is_employee;
            this.isManager = r.data.is_manager;
            this.profile_image_url = r.data.profile_image_url;

            // work related
            this.workPhoneNum = r.data.work_phone_num;
            this.workPhoneISD = this.getISDStuff(r.data.work_phone_isd);
            this.jobTitle = r.data.job_title;
            this.location = r.data.job_location;
            this.workEmail = r.data.work_email;
            this.startDate = this.isDefaultDate(r.data.start_date)
              ? "" //new Date().toISOString().substring(0, 10)
              : r.data.start_date
              ? r.data.start_date.substring(0, 10)
              : ""; //new Date().toISOString().substring(0, 10);
            // this.formatFromDate(r.data.start_date);
            this.employmentType = r.data.employment_type;
            this.department = r.data.department;
            this.manager = r.data.manager;
            this.payFrequency = r.data.pay_frequency;
            this.salary = r.data.salary;
            this.percentageEmployed = r.data.percentage_employed;

            // bank Info
            this.bankName = r.data.bank_name;
            this.bankAccountNumber = r.data.bank_account_number;
            this.ssnNumber = r.data.ssn_number;
            this.tnc_complete = r.data.tnc_complete;
          }
        })
        .catch((err) => {
          console.error(Constants.Employee.Messages.Error.Get, err);
          this.snackbarMessage = Constants.Employee.Messages.Error.Get;
          this.snackbar = true;
        });
    },
    sendInvite() {
      if (this.isDemo) {
        return false;
      }
      let payload = {
        id: this.$route.params.id,
        email: this.email,
        companyID: this.userInfo.companyID,
        namespace: this.userInfo.namespace,
      };
      this.$store
        .dispatch(Actions.SendInvite, payload)
        .then((r) => {
          if (r.status === 200) {
            this.snackbar = true;
            this.snackbarMessage = `Activation email sent to ${this.email}`;
          }
        })
        .catch((e) => {
          console.error(e);
          this.snackbar = true;
          this.snackbarMessage =
            "Could not send Activation email to " + this.email;
        });
    },
    deleteEntity() {
      if (!this.userInfo.role || this.userInfo.role !== Constants.Roles.HR) {
        return false;
      }
      this.$store
        .dispatch(Actions.Employee.Delete, this.$route.params.id)
        .catch((err) => {
          console.error(Constants.Employee.Messages.Error.Delete, err);
          this.snackbarMessage = Constants.Employee.Messages.Error.Delete;
          this.snackbar = true;
        })
        .finally(() => {
          this.deleteWorkflow = false;
          this.$router.push({ name: Component.Protected.Employee.Base });
        });
    },
    getManagers() {
      let payload = { type: Constants.Employee.Type.Active };
      this.$store.dispatch(Actions.Employee.Find, payload).then((r) => {
        if (r.status === 200) this.managers = r.data;
      });
    },
    getTimeline() {
      this.$store
        .dispatch(Actions.Employee.Timeline, this.$route.params.id)
        .catch((err) => {
          console.error(Constants.Employee.Messages.Error.Timeline, err);
          this.snackbarMessage = Constants.Employee.Messages.Error.Timeline;
          this.snackbar = true;
        });
    },
    setup() {
      this.phoneValidator = libphonenumber.PhoneNumberUtil.getInstance();
      this.resetModels();
      this.getInfo();
      this.getManagers();
      this.getEmployee();
      this.getTimeline();
      this.getWorkflows();
      this.getPersonal();
    },
  },
  created() {
    if (!this.$route.params.id) {
      this.$router.go(-1);
      return false;
    }

    if (this.userInfo && this.userInfo.role) {
      this.setup();
    } else {
      this.$store.dispatch(Actions.Firebase.UserInfo).then(() => this.setup());
    }
  },
};
</script>
<style scoped>
* >>> div.v-picker__title {
  background-color: #551738 !important;
  border-color: #551738 !important;
}

/* * >>> div.v-date-picker-table {
  height: 250px !important;
} */

.border-gray {
  border: lightgray solid 1px;
}

* >>> div.v-item-group {
  border-top-left-radius: 3px;
  border-top-right-radius: 3px;
}

* >>> div.v-slide-group__next > i,
* >>> div.v-slide-group__prev > i {
  color: #eceff1 !important;
}
</style>